package com.snakesworld.game.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.snakesworld.game.engine.SNAKE_SKIN_OPTIONS
import com.snakesworld.game.engine.SnakeSkinOption
import com.snakesworld.game.network.AuthState
import com.snakesworld.game.network.MultiplayerRepository
import com.snakesworld.game.network.NicknameValidator
import com.snakesworld.game.network.UserProfile
import com.snakesworld.game.network.isEphemeralUserId
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.random.Random

data class MenuUiState(
    val callsign: String = "VIPER_99",
    val selectedSkin: SnakeSkinOption = SNAKE_SKIN_OPTIONS.first(),
    val profile: UserProfile = UserProfile(),
    val isValidCallsign: Boolean = true,
)

class MenuViewModel(
    private val repository: MultiplayerRepository,
    private val customScope: CoroutineScope? = null,
) : ViewModel() {

    private val scope: CoroutineScope
        get() = customScope ?: viewModelScope

    private val _uiState = MutableStateFlow(MenuUiState())
    val uiState: StateFlow<MenuUiState> = _uiState.asStateFlow()

    private val arcadeNames = listOf(
        "NEON_VIPER", "CYBER_GHOST", "LASER_FANG", "GRID_SHADOW",
        "TITAN_8BIT", "TURBO_REX", "PHANTOM_ACE", "BLAZE_FOX",
        "VOID_RUNNER", "HYPER_DRAKE", "RETRO_FALCON", "COSMO_WORM"
    )

    private var authObserverJob: Job? = null
    private val profileMutex = Mutex()
    /** Bumped when the account is erased so an in-flight profile load cannot recreate it. */
    private var profileEpoch = 0

    init {
        authObserverJob = viewModelScope.launch {
            repository.authState.collect { authState ->
                if (authState is AuthState.Authenticated) {
                    loadProfileForUser(authState.uid)
                }
            }
        }
        loadProfile()
    }

    override fun onCleared() {
        super.onCleared()
        authObserverJob?.cancel()
    }

    fun loadProfile() {
        val currentUid = (repository.authState.value as? AuthState.Authenticated)?.uid
        scope.launch {
            if (currentUid != null) {
                loadProfileForUser(currentUid)
            } else {
                val existing = _uiState.value.profile
                if (existing.nickname.isBlank()) {
                    val defaultNickname = arcadeNames.random()
                    _uiState.value = _uiState.value.copy(
                        callsign = defaultNickname,
                        profile = existing.copy(nickname = defaultNickname),
                        isValidCallsign = true
                    )
                }
            }
        }
    }

    private suspend fun loadProfileForUser(uid: String) {
        val epoch = profileEpoch
        val existing = repository.getUserProfile(uid)
        if (epoch != profileEpoch) return
        val currentCallsign = _uiState.value.callsign.takeIf { it.isNotBlank() }
        val defaultNickname = currentCallsign ?: arcadeNames.random()
        val nickname = existing?.nickname?.takeIf { it.isNotBlank() } ?: defaultNickname
        val base = (existing ?: UserProfile(uid = uid, nickname = nickname)).copy(
            uid = uid,
            nickname = nickname,
        )
        // A refresh can return while a block is being saved. Merge at the moment
        // of the write so an in-flight load cannot drop ids chosen in this session.
        if (epoch != profileEpoch) return
        _uiState.update { state ->
            if (epoch != profileEpoch) return@update state
            val localBlocked = state.profile.takeIf { it.uid == uid }?.blockedIds.orEmpty()
            val merged = base.copy(
                blockedIds = (base.blockedIds + localBlocked).distinct().takeLast(64),
            )
            state.copy(
                callsign = merged.nickname,
                profile = merged,
                isValidCallsign = NicknameValidator.isAllowed(merged.nickname),
            )
        }
        if (existing != null) {
            profileMutex.withLock {
                if (epoch != profileEpoch) return@withLock
                val remote = repository.getUserProfile(uid) ?: return@withLock
                val latest = _uiState.value.profile
                if (latest.uid != uid) return@withLock
                val blocked = (remote.blockedIds + latest.blockedIds).distinct().takeLast(64)
                repository.saveUserProfile(remote.copy(nickname = latest.nickname, blockedIds = blocked))
                _uiState.update { state ->
                    if (epoch != profileEpoch || state.profile.uid != uid) {
                        state
                    } else {
                        state.copy(
                            profile = state.profile.copy(
                                blockedIds = (state.profile.blockedIds + blocked).distinct().takeLast(64),
                            ),
                        )
                    }
                }
            }
        }
    }

    /**
     * Resets personal arcade stats (High Score, Games Played, Best Kills, Best Length) to 0.
     */
    fun resetUserData() {
        profileEpoch += 1
        val current = _uiState.value.profile
        val currentCallsign = _uiState.value.callsign.ifBlank { current.nickname }
        // Drop uid so onCallsignChanged / saveUserProfile cannot recreate the erased Firestore doc.
        val cleared = UserProfile(
            uid = "",
            nickname = currentCallsign,
            gamesPlayed = 0,
            bestScore = 0,
            bestKills = 0,
            bestLength = 0,
            spGamesPlayed = 0,
            spBestScore = 0,
            spBestKills = 0,
            spBestLength = 0,
            markDelete = false,
        )
        _uiState.value = _uiState.value.copy(
            profile = cleared,
            callsign = currentCallsign,
            isValidCallsign = NicknameValidator.isAllowed(currentCallsign),
        )
    }

    /**
     * Clears all remote records for this user and resets local arcade stats to 0.
     */
    fun clearUserData(onComplete: (Boolean) -> Unit = {}) {
        val currentUid = (repository.authState.value as? AuthState.Authenticated)?.uid
            ?: _uiState.value.profile.uid
        scope.launch {
            val result = repository.clearUserData(currentUid)
            if (result.isSuccess) {
                resetUserData()
                onComplete(true)
            } else {
                onComplete(false)
            }
        }
    }

    fun onCallsignChanged(newCallsign: String) {
        val trimmed = newCallsign.take(16).uppercase()
        val isValid = NicknameValidator.isAllowed(trimmed)
        val updatedProfile = _uiState.value.profile.copy(nickname = trimmed)
        _uiState.value = _uiState.value.copy(
            callsign = trimmed,
            profile = updatedProfile,
            isValidCallsign = isValid
        )
        if (isValid && updatedProfile.uid.isNotBlank()) {
            scope.launch {
                repository.saveUserProfile(updatedProfile)
            }
        }
    }

    fun randomizeCallsign() {
        val randomName = arcadeNames.random()
        val number = Random.nextInt(10, 99)
        val name = "${randomName}_$number"
        onCallsignChanged(name)
    }

    fun blockPlayer(targetId: String) {
        if (targetId.isBlank()) return
        _uiState.update { state ->
            val profile = state.profile
            if (targetId == profile.uid || targetId in profile.blockedIds) {
                state
            } else {
                state.copy(
                    profile = profile.copy(
                        blockedIds = (profile.blockedIds + targetId).distinct().takeLast(64),
                    ),
                )
            }
        }
        val updated = _uiState.value.profile
        if (targetId !in updated.blockedIds) return
        if (updated.uid.isBlank() || isEphemeralUserId(updated.uid)) return
        scope.launch {
            profileMutex.withLock {
                val latest = _uiState.value.profile
                if (latest.uid.isBlank() || isEphemeralUserId(latest.uid)) return@withLock
                val remoteBlocked = repository.getUserProfile(latest.uid)?.blockedIds.orEmpty()
                val blocked = (remoteBlocked + latest.blockedIds).distinct().takeLast(64)
                repository.saveUserProfile(latest.copy(blockedIds = blocked))
                _uiState.update { state ->
                    if (state.profile.uid != latest.uid) {
                        state
                    } else {
                        state.copy(
                            profile = state.profile.copy(
                                blockedIds = (state.profile.blockedIds + blocked).distinct().takeLast(64),
                            ),
                        )
                    }
                }
            }
        }
    }

    fun reportPlayer(targetId: String, targetName: String) {
        if (targetId.isBlank()) return
        scope.launch {
            repository.reportPlayer(targetId, targetName)
        }
    }

    fun onSkinSelected(skinId: String) {
        val skin = SNAKE_SKIN_OPTIONS.find { it.id == skinId } ?: SNAKE_SKIN_OPTIONS.first()
        _uiState.value = _uiState.value.copy(selectedSkin = skin)
    }
}
